<template>
  <div class="pb-4">
    <div class="mt-2 border border-zt-dark-300 rounded px-2 pt-1 relative">
      <label
        class="px-2 text-zt-dark-300 text-zt-xs bg-white absolute top--negative__8"
        >
        {{ $t('message.searchComponent.eforms') }}
      </label
      >
      <div>
        <MultiSelect
          :value="eformSelected"
          @input="setEformSelected"
          :options="eformOpts"
          :searchable="true"
          label="name"
          :multiple="true"
          :internal-search="true"
          :clear-on-select="true"
          :close-on-select="false"
          :preserve-search="false"
          :show-labels="false"
          :show-no-options="false"
          placeholder=""
          track-by="id"
          data-vs-select="eforms-opt"
        >
          <template slot="noResult"
            ><span class="multiselect__single">{{
              $t('message.searchComponent.not_founded')
            }}</span></template
          >
          <template slot="maxElements"
            ><span class="multiselect__single">{{
              $t('message.searchComponent.max_elements')
            }}</span></template
          >
          <template #tag="{ remove, option }">
            <CustomChip @click="remove(option)">{{ option.name }}</CustomChip>
          </template>
        </MultiSelect>
      </div>
    </div>
    <div class="flex pt-4">
      <div class="flex-1">
        <div class="pr-1">
          <v-date-picker
            v-model="datePickerStart.date"
            :masks="masks"
            @input="setInputStartPicker"
            :max-date="maxStartDate"
            :popover="{ visibility: 'click' }"
          >
            <template v-slot="{ inputValue, inputEvents }">
              <div class="border border-zt-dark-300 rounded px-2 pt-1 relative">
                <label
                  class="px-2 text-zt-dark-300 text-zt-xs bg-white absolute top--negative__8"
                  >{{ languages.date.from }}</label
                >
                <div>
                  <div>
                    <input
                      v-on="inputEvents"
                      :value="inputValue"
                      type="text"
                      class="outline-none py-2 w-full"
                      data-vs-input="input-events"
                    />
                  </div>
                </div>
              </div>
            </template>
          </v-date-picker>
        </div>
      </div>
      <div class="flex-1">
        <div class="pl-1">
          <v-date-picker
            v-model="datePickerEnd.date"
            :masks="masks"
            @input="setInputEndPicker"
            :max-date="nowDate"
            :min-date="datePickerStart.date"
            :popover="{ visibility: 'click' }"
          >
            <template v-slot="{ inputValue, inputEvents }">
              <div class="border border-zt-dark-300 rounded px-2 pt-1 relative">
                <label
                  class="px-2 text-zt-dark-300 text-zt-xs bg-white absolute top--negative__8"
                  >{{ languages.date.to }}</label
                >
                <div>
                  <div>
                    <input
                      v-on="inputEvents"
                      :value="inputValue"
                      type="text"
                      class="outline-none py-2 w-full"
                      data-vs-input="input-events"
                    />
                  </div>
                </div>
              </div>
            </template>
          </v-date-picker>
        </div>
      </div>
      <div v-show="isResetDatesVisible" class="px-2 inline-flex items-center">
        <button
          @click="clearDateInputs"
          class="text-zt-primary-200 text-zt-xl focus:outline-none"
          data-vs-button="input-events"
        >
          <zt-icon name="clear-date" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import CustomChip from './CustomChip.vue'
import MultiSelect from 'vue-multiselect'
import { filter, map, isEmpty } from 'lodash'
import moment from 'moment'
import { addFilters } from './utils'

const formatInput = (date, format) => {
  return moment(date).format(format)
}

const sendGTMCustomEvent = (event) => {
  window.dataLayer.push({
    event,
    action: 'Click',
    value: 500,
  })
}

export default {
  name: 'SearchEformSection',
  inject: ['store', 'setFilters', 'setHistoryParams', 'existHistoryProperty'],
  components: {
    MultiSelect,
    CustomChip,
  },
  data() {
    return {
      masks: {
        input: 'DD/MM/YYYY',
      },
      nowDate: new Date(),
      eformSelected: [],
      datePickerStart: {
        date: '',
        dateInverterStart: '',
        menu: false,
      },
      datePickerEnd: {
        date: '',
        dateInverterEnd: '',
        menu: false,
      },
      languages: {
        date: {
          from: this.$t('message.searchComponent.created.from'),
          to: this.$t('message.searchComponent.created.to')
        },
      },
    }
  },
  computed: {
    eformOpts() {
      if (this.store.foldersItems.length > 0) {
        const eforms = filter(this.store.foldersItems, { type: 'eforms' })
        return !isEmpty(eforms) ? eforms[0].childs : []
      }
      return []
    },
    setInputStartDate() {
      if (!this.datePickerStart.date) {
        return null
      }
      return formatInput(this.datePickerStart.date, 'YYYY-MM-DD')
    },
    setInputEndDate() {
      if (!this.datePickerEnd.date) {
        return null
      }
      return formatInput(this.datePickerEnd.date, 'YYYY-MM-DD')
    },
    maxStartDate() {
      if (this.datePickerEnd.date) {
        return this.datePickerEnd.date
      }

      return this.nowDate
    },
    isResetDatesVisible() {
      return !!this.datePickerStart.date || !!this.datePickerEnd.date
    },
  },
  created() {
    if (this.existHistoryProperty('eform')) {
      this.eformSelected = this.store.history.params.eform
    }
    if (this.existHistoryProperty('publishDateFrom')) {
      const start = this.store.history.params.publishDateFrom
      this.datePickerStart.date = moment(start).toDate()
    }
    if (this.existHistoryProperty('publishDateTo')) {
      const end = this.store.history.params.publishDateTo
      this.datePickerEnd.date = moment(end).toDate()
    }
  },
  methods: {
    setEformSelected(value) {
      sendGTMCustomEvent('zt-gtm-documents-search-digitalformsfilter')
      this.eformSelected = value
      this.setStoreFilter(['eform', map(this.eformSelected, 'id')])

      const { history: { params } } = this.store
      this.setHistoryParams(
        addFilters(
          { ...params },
          ['eform', ...[this.eformSelected]]
        )
      )
    },
    setInputStartPicker() {
      this.changePicker(this.setInputStartDate, 'publishDateFrom')
    },
    setInputEndPicker() {
      this.changePicker(this.setInputEndDate, 'publishDateTo')
    },
    setStoreFilter(...args) {
      const { filters } = this.store
      const newFilters = addFilters({ ...filters }, ...args)
      this.setFilters(newFilters)
    },
    changePicker(value, key) {
      this.setStoreFilter([key, value])

      const { history: { params } } = this.store
      this.setHistoryParams(
        addFilters(
          { ...params },
          [key, ...[value]]
        )
      )
    },
    clearDateInputs() {
      this.datePickerStart.date = ''
      this.datePickerEnd.date = ''
      this.setStoreFilter(['publishDateFrom', ''], ['publishDateTo', ''])

      const { history: { params } } = this.store
      this.setHistoryParams(
        addFilters(
          { ...params },
          ['publishDateFrom', ''],
          ['publishDateTo', ''],
        )
      )
    },
    cleanFilters() {
      this.eformSelected = []
    }
  },
}
</script>

<style scoped lang="scss">
  ::v-deep .multiselect__content-wrapper {
    box-shadow: 0 5px 5px -3px rgba(0,0,0,.2), 0 8px 10px 1px rgba(0,0,0,.14), 0 3px 14px 2px rgba(0,0,0,.12);
  }
</style>